import { useContext, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PageContext from "./PageContext";
import { HttpContext } from '../../api-services/HttpService';
import ApiRequestButton from './ApiRequestButton';
import CCOffcanvas from './CCOffcanvas.tsx';
import ButtonComponent from './ButtonComponent.jsx';
import ModalAutoComplete from './ModalAutoComplete.jsx'; 
import MultiSelectAutocomplete from './MultiSelectAutocompleteCopy.jsx';
import OrderedDataTable from './OrderedDataTableCopy.jsx';
import RebateSetupRow from './RebateSetupRow.jsx';
import { styled, Typography } from '@mui/material';
import BannerAlert from './BannerAlertCopy.jsx';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

//Styled Topography for table name
const TableName = styled(Typography)(({ theme }) => ({
    // background: "#E6F6FA",
    // color: "#3D3D3D",
    background: process.env.REACT_APP_LABEL_COLOR,
    color: `${process.env.REACT_APP_LABEL_TEXT_COLOR} !important`,
    justifyContent: "center",
    fontSize: "14px",
    minHeight: "40px",
    lineHeight: "24px",
    display: "flex",
    alignItems: "center",
    fontFamily:"Roboto medium, Helvetica, Arial, sans-serif"
}));

const CopyRebateSetupModal = () => {
    // get context from parent
    const { 
        showCopyRebateSetupModal, 
        setShowCopyRebateSetupModal, 
        subCompanyList, 
        snackbarAlert, 
        getRefreshData
    } = useContext(PageContext);
    // initialize http context 
    const {
        GET, 
        PUT
    } = useContext(HttpContext);
    // get persistant redux global state from store
    const company = useSelector((state) => state.company);
    const companyInfo = useSelector((state) => state.companyInfo); 
    // initialize state
    const [sourceCompanyId, setSourceCompanyId] = useState(null); 
    const [destinationCompanyIds, setDestinationCompanyIds] = useState([]); 
    const [destinationCompanyList, setDestinationCompanyList] = useState([]); 
    const [copyToAll, setCopyToAll] = useState(false); 
    // state for rebate setups table 
    const [rebateSetupsDataList, setRebateSetupsDataList] = useState([]); 
    const [rebateSetupsTableHeader, setRebateSetupsTableHeader] = useState([]); 
    const [showSpinner, setShowSpinner] = useState(false); 

    // function wrapper to pass row component to datatable
    const RebateSetupRowComponent = (row, count, tableHeader, stickyEnabled) =>{
        return <RebateSetupRow row={row} count={count} tableHeader={tableHeader} enableEdit={false} stickyEnabled={stickyEnabled} />; 
    }

    // function to retrive rebate setup data 
    const getRebateSetupData = () => {
        setShowSpinner(showSpinner => showSpinner = true); 
        return new Promise((resolve, reject) => {
            GET('/client/companyIdRebateSetups', {
                mastercompanyid: companyInfo['mastercompanyid'], 
                companyid: sourceCompanyId
            }).then(res => {    
                // set state for table 
                setRebateSetupsDataList(rebateSetupsDataList => rebateSetupsDataList = res['data']['RebateSetupsData']); 
                setRebateSetupsTableHeader(rebateSetupsTableHeader => rebateSetupsTableHeader = res['data']['RebateSetupsCols']); 
                // hide spinner
                setShowSpinner(showSpinner => showSpinner = false); 
                // resolve on success
                resolve(res); 
            }).catch(err => {
                // hide spinner
                setShowSpinner(showSpinner => showSpinner = false); 
                // reject on error
                reject(err); 
            }); 
        }); 
    }

    // event handler for submit click 
    const onSubmitClick = () => {
        return new Promise ((resolve, reject) => {
            // validate source company id
            if (!subCompanyList.includes(sourceCompanyId)) {
                snackbarAlert(true, 'Invalid Source Company ID.', 'error'); 
                reject(); 
                return; 
            }
            // validate all destination company ids
            for (let i = 0; i < destinationCompanyIds.length; i++) {
                // check that it exists in sub company list 
                if (!subCompanyList.includes(destinationCompanyIds[i])) {
                    snackbarAlert(true, 'Invalid Destination Company ID: ' + destinationCompanyIds[i].toString() + '.', 'error'); 
                    reject(); 
                    return; 
                }
                // source company ID cannot exist in destination company IDs
                if (sourceCompanyId === destinationCompanyIds[i]) {
                    snackbarAlert(true, 'Invalid Destination Company ID: ' + destinationCompanyIds[i].toString() + '.', 'error'); 
                    reject(); 
                    return; 
                }
            }
            // use function to make put request
            let response; 
            PUT('/client/copyRebateSetups', {
                sourcemastercompanyid: companyInfo['mastercompanyid'], 
                sourcecompanyid: sourceCompanyId, 
                destinationmastercompanyid: companyInfo['mastercompanyid'], 
                destinationcompanyids: destinationCompanyIds
            }).then(res => {
                console.log(res);
                response = res; 
                // default state
                setSourceCompanyId(sourceCompanyId => sourceCompanyId = null); 
                setDestinationCompanyIds(destinationCompanyIds => destinationCompanyIds = []); 
                setDestinationCompanyList(destinationCompanyList => destinationCompanyList = subCompanyList);
                // close modal 
                setShowCopyRebateSetupModal(showCopyRebateSetupModal => showCopyRebateSetupModal = false); 
                // refresh data 
                return getRefreshData(); 
            }).then(res => {
                console.log(res); 
                // resolve on success
                resolve(response); 
            }).catch(err => {
                console.log(err);
                // show alert on error
                snackbarAlert(true, 'Error uploading data.', 'error');
                // reject on error
                reject(err); 
            }); 
        }); 
    }

    // useEffect to run on source company change 
    useEffect(() => {
        // get data for rebate setups table 
        getRebateSetupData().then(res => {
            console.log(res); 
        }).catch(err => {
            console.log(err); 
        }); 
    }, [sourceCompanyId]); 

    // update destination summary list on sub company list change or source company change
    useEffect(() => {
        // Remove source company from options list
        let temp1 = []; 
        for (let i = 0; i < subCompanyList.length; i++) {
            if (subCompanyList[i].toString() !== sourceCompanyId){
                temp1.push(subCompanyList[i]);    
            }
        }
        // Remove source company from selected destination companies
        let temp2 = []; 
        if (copyToAll){
            temp2 = [...temp1];
        }
        else {
            temp2 = [...destinationCompanyIds];
        }
        for (let i = 0; i < temp2.length; i++ ){
            // check if element is equal to source company
            if (temp2[i].toString() === sourceCompanyId){
                // remove from list
                temp2 = temp2.slice(0, i).concat(temp2.slice(i+1, temp2.length)); 
                // decrement i
                i--; 
            }
        }
        // set state
        setDestinationCompanyList(destinationCompanyList => destinationCompanyList = temp1); 
        setDestinationCompanyIds(destinationCompanyIds => destinationCompanyIds = temp2); 
    }, [subCompanyList, sourceCompanyId]); 

    // update destination list on copy to all checkbox change
    useEffect(() => {
        if (copyToAll) {
            setDestinationCompanyIds(destinationCompanyIds => destinationCompanyIds = destinationCompanyList); 
        }
    }, [copyToAll]); 

    // render
    return(
        <> 
            <CCOffcanvas
                title='Copy Rebate Setup'
                show={showCopyRebateSetupModal}
                onCanvasClose={()=>setShowCopyRebateSetupModal(showCopyRebateSetupModal => showCopyRebateSetupModal = false)}
                bodyCssClassName="offcanvas-body bg-white p-3 pt-2"
                offCanvasClassName="offcanvas-parent-card-50"
                >
                    <div className="d-flex flex-column h-100">
                        <div className="d-flex flex-column gap-3 flex-grow-1 overflow-scroll">
                            {/* source company autocomplete */}
                            <div>
                                <ModalAutoComplete
                                    tag='Source Company ID' 
                                    width='95%' 
                                    value={sourceCompanyId} 
                                    setValue={setSourceCompanyId} 
                                    list={subCompanyList}  
                                    topAlignTypography={true}
                                />
                            </div>
                            {/* rebate setups of source company */}
                            <div className='mt-2'>
                                <TableName>Source Rebate Setups</TableName>
                                <OrderedDataTable varHeight={'605px'} RowComponent={RebateSetupRowComponent} tableHeader={rebateSetupsTableHeader} dataList={rebateSetupsDataList} showSpinner={showSpinner} numToPage={25} skeletonHeight='28vh' sortingEnabled={true} stickyFirstColumnEnabled={true} />
                            </div>
                            {/* checkbox to select all companies */}
                            <div>
                                <FormControlLabel
                                    value="end"
                                    control={
                                        <Checkbox
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: '#000000',
                                                },
                                            }} 
                                        />}
                                    label={"Copy to all Company IDs"}
                                    labelPlacement="end"
                                    checked={copyToAll}
                                    onChange={(e)=>{setCopyToAll(copyToAll => copyToAll = e.target.checked)}}
                                />
                            </div> 
                            {/* destination company multiselect autocomplete */}
                            <div>
                                <MultiSelectAutocomplete 
                                    tag='Destination Company IDs' 
                                    width='95%' 
                                    values={destinationCompanyIds} 
                                    setValues={setDestinationCompanyIds} 
                                    list={destinationCompanyList}  
                                    topAlignTypography={true}
                                />
                            </div>
                            <div>
                                <BannerAlert disableClose show={true} type='info' message={'Copying rebate setups will de-activate all Destination Company ID rebate setups and replace them with Source Company ID rebate setups.'} width='100%' /> 
                            </div>
                        </div>
                        {/* button footer */}
                        <div
                            className="d-flex justify-content-end p-3 w-100 offcanvas-footer z-2" style={{height: '65px'}}
                        >
                            <ButtonComponent tag={"Cancel"} 
                                border={'#fcd800 1px solid'} background={'#fff'} color={'#001317'} 
                                onButtonClick={()=>setShowCopyRebateSetupModal(showCopyRebateSetupModal => showCopyRebateSetupModal = false)}
                            />  
                            <ApiRequestButton onButtonClick={onSubmitClick} />  
                        </div>
                    </div>
            </CCOffcanvas>
        </>
    );
}

export default CopyRebateSetupModal; 